/*
This computer program, as defined in the Copyright, Designs and Patents Act 1998 and the Software Directive (2009/24/EC), 
is the copyright of Logic Valley Ltd, a wholly owned subsidiary of Marston (Holdings) Ltd. All rights are reserved.
*/
import React, { useState, useMemo, useCallback, useEffect } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useRecoilValue, useSetRecoilState } from 'recoil'
import { ReactSVG } from 'react-svg'
import { useConfirm } from 'material-ui-confirm'

import TabControl from '../../components/base/tabControl/TabControl'
import ReviewTab from './features/reviewTab'
import ManualReviewMovements from './features/manualReviewMovements'
import ManualReviewEvents from './features/manualReviewEvents'
import { useTrackAnprLastActivity } from '@components/custom/anprLastActivity'

import {
  madeChanges,
  observationDetailsAtom,
  showLoading,
  toastMessage,
} from '../../components/recoil/atoms/atoms'
import { fetchDataSelector } from '../../components/recoil/selectors/selectors'
import { ConfirmPopupRendererObject } from '../../components/custom/confirmPopupRenderer/ConfirmPopupRenderer'

import backArrow from '../../../assets/static/backArrow.svg'
import holdIcon from '../../../assets/static/holdReview.svg'

import utils from '../../../utils/utils'
import { ManualReviewAPIEndpoints } from './api'
import { ROUTES, MESSAGES, PAGE_CONFIG, TASK_STATUS } from './constants'
import { getSafeValue, getObservationId, formatTemplate } from './utils'

import './ManualReviewDetailPage.css'

/* =========================
   Component
========================= */

const ManualReviewDetailPage = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const confirm = useConfirm()
  const observationDetails = useRecoilValue(observationDetailsAtom)
  const setObservationDetails = useSetRecoilState(observationDetailsAtom)
  const hasChanges = useRecoilValue(madeChanges)
  const fetchCoreData = useRecoilValue(fetchDataSelector)
  const setShowLoading = useSetRecoilState(showLoading)
  const setToastMessage = useSetRecoilState(toastMessage)
  const [tabIndex, setTabIndex] = useState(0)

  /* =========================
     Derived Values
  ========================= */

  const caseNumber = useMemo(
    () =>
      getSafeValue(
        observationDetails,
        PAGE_CONFIG.manualReviewDetail?.fields?.caseNumber
      ),
    [observationDetails]
  )

  const status = useMemo(
    () =>
      getSafeValue(
        observationDetails,
        PAGE_CONFIG.manualReviewDetail?.fields?.status
      ),
    [observationDetails]
  )

  const displayStatus = useMemo(() => {
    return status === TASK_STATUS?.HOLD ? TASK_STATUS?.ON_HOLD_LABEL : status
  }, [status])

  const isOnHold = useMemo(() => {
    return status === TASK_STATUS?.HOLD
  }, [status])

  const currentObservationId = useMemo(() => {
    return getObservationId(observationDetails)
  }, [observationDetails])
  const requestedObservationId = useMemo(() => {
    const raw = new URLSearchParams(location?.search).get('observationId')
    return raw ? utils?.decryptData(raw) || raw : null
  }, [location?.search])

  const postDiscardRoute = useMemo(() => {
    const sourceRoute = location?.state?.from?.toLowerCase()
    const taskStatus = observationDetails?.taskStatus?.toLowerCase()
    return sourceRoute === ROUTES?.ON_HOLD?.replace('/', '') ||
      taskStatus === TASK_STATUS?.HOLD_LOWER
      ? ROUTES?.ON_HOLD
      : ROUTES?.MANUAL_REVIEW
  }, [location?.state?.from, observationDetails?.taskStatus])

  useTrackAnprLastActivity()

  useEffect(() => {
    if (!requestedObservationId) {
      return
    }
    if (`${currentObservationId || ''}` === `${requestedObservationId}`) {
      return
    }
    let isMounted = true
    const loadObservationDetails = async () => {
      setShowLoading(true)
      try {
        const response = await fetchCoreData(
          ManualReviewAPIEndpoints?.getObservationDetailsById?.method,
          ManualReviewAPIEndpoints?.getObservationDetailsById?.url.replace(
            '__observationId__',
            requestedObservationId
          ),
          null,
          null,
          null,
          false,
          utils?.apiDomainName?.privateLand?.name
        )
        if (!isMounted) {
          return
        }
        if (response?.status === utils?.apiResponseCode?.successCode) {
          setObservationDetails(response?.data)
          return
        }
        setToastMessage({
          message:
            response?.message || MESSAGES?.holdReview?.invalidObservation,
          status: utils?.toastStatus?.error,
        })
      } catch {
        if (!isMounted) {
          return
        }
        setToastMessage({
          message: utils?.errorOnGettingData,
          status: utils?.toastStatus?.error,
        })
      } finally {
        if (isMounted) {
          setShowLoading(false)
        }
      }
    }
    loadObservationDetails()
    return () => {
      isMounted = false
    }
  }, [
    currentObservationId,
    fetchCoreData,
    requestedObservationId,
    setObservationDetails,
    setShowLoading,
    setToastMessage,
  ])

  const pageTitle = useMemo(
    () =>
      formatTemplate(PAGE_CONFIG?.manualReviewDetail?.pageTitleTemplate, {
        caseNumber,
      }),
    [caseNumber]
  )

  /* =========================
     Handlers
  ========================= */

  const handleBack = useCallback(async () => {
    if (hasChanges) {
      try {
        await confirm({
          ...ConfirmPopupRendererObject(null, utils?.dataSaveWarnMessage),
        })
      } catch (error) {
        return
      }
    }

    const sourceRoute = location?.state?.from?.toLowerCase()
    const taskStatus = observationDetails?.taskStatus?.toLowerCase()

    const backRoute =
      sourceRoute === ROUTES?.ON_HOLD?.replace('/', '') ||
      taskStatus === TASK_STATUS?.HOLD_LOWER
        ? ROUTES?.ON_HOLD
        : sourceRoute === ROUTES?.DISCARDED_CASES?.replace('/', '') ||
          sourceRoute === TASK_STATUS?.DISCARDED_LOWER ||
          taskStatus === TASK_STATUS?.DISCARDED_LOWER
        ? ROUTES?.DISCARDED_CASES
        : ROUTES?.MANUAL_REVIEW

    navigate(backRoute)
  }, [
    confirm,
    hasChanges,
    navigate,
    location?.state?.from,
    observationDetails?.taskStatus,
  ])

  const handleHoldReview = useCallback(async () => {
    try {
      await confirm(
        ConfirmPopupRendererObject(null, MESSAGES?.holdReview?.confirm)
      )

      const observationId = getObservationId(observationDetails)

      if (!observationId) {
        setToastMessage({
          message: MESSAGES?.holdReview?.invalidObservation,
          status: utils?.toastStatus?.error,
        })
        return
      }

      const endpoint = ManualReviewAPIEndpoints.updateHoldReview
      const url = endpoint?.url.replace('__observationId__', observationId)

      const response = await fetchCoreData(
        endpoint?.method,
        url,
        null,
        null,
        null,
        false,
        utils?.apiDomainName?.privateLand?.name
      )

      if (response?.status === utils?.apiResponseCode?.successCode) {
        setToastMessage({
          message: response?.data?.message || MESSAGES?.holdReview?.success,
          status: utils?.toastStatus?.success,
        })
        navigate(ROUTES?.MANUAL_REVIEW)
        return
      }

      setToastMessage({
        message: response?.data?.message || MESSAGES?.holdReview?.failed,
        status: utils?.toastStatus?.error,
      })
    } catch (error) {
      // User cancel OR API error
      if (error?.response) {
        setToastMessage({
          message:
            error?.response?.data?.message || MESSAGES?.holdReview?.error,
          status: utils?.toastStatus?.error,
        })
      }
    }
  }, [confirm, observationDetails, fetchCoreData, setToastMessage, navigate])

  /* =========================
     Tab Data
  ========================= */

  const tabData = useMemo(
    () => [
      {
        tabTitle: 'Review',
        tabContent: (
          <ReviewTab
            observationDetails={observationDetails}
            postDiscardRoute={postDiscardRoute}
          />
        ),
        dataTestId: 'reviewTab',
      },
      {
        tabTitle: 'Movements',
        tabContent: (
          <ManualReviewMovements observationDetails={observationDetails} />
        ),
        dataTestId: 'movementsTab',
      },
      {
        tabTitle: 'Events',
        tabContent: (
          <ManualReviewEvents observationDetails={observationDetails} />
        ),
        dataTestId: 'eventsTab',
      },
    ],
    [observationDetails, postDiscardRoute]
  )

  /* =========================
     Render
  ========================= */

  return (
    <div className="manual-review-detail-page">
      <div className="manual-review-page__header">
        <div className="manual-review-page__header-left">
          {observationDetails?.taskStatus !== TASK_STATUS?.ISSUED && (
            <img
              src={backArrow}
              alt="Back"
              onClick={handleBack}
              className="manual-review-page__back-icon"
              width={19}
              height={19}
            />
          )}
          <p className="manual-review-page__title">{pageTitle}</p>
          <span
            className={`manual-review-page__status-badge ${
              isOnHold
                ? 'on-hold'
                : displayStatus?.toLowerCase() === TASK_STATUS?.DISCARDED_LOWER
                ? 'discarded'
                : ''
            }`}
          >
            {displayStatus}
          </span>
        </div>
        {tabIndex === 0 &&
          observationDetails?.taskStatus === TASK_STATUS?.ACTIVE && (
            <div
              className="manual-review-page__header-right"
              onClick={handleHoldReview}
            >
              <ReactSVG
                className="manual-hold-Icon reactSvgDiv"
                src={holdIcon}
              />
              <div className="manual-review-page__action-label">
                {PAGE_CONFIG?.manualReviewDetail?.holdAction?.label}
              </div>
            </div>
          )}
      </div>

      <div className="manual-review-page__tabs">
        <TabControl
          datasource={tabData}
          selectedTabIndex={tabIndex}
          onChangeHandler={(_, index) => setTabIndex(index)}
        />
      </div>
    </div>
  )
}

export default ManualReviewDetailPage
